import { Button, Menu } from "@mantine/core";
import { IconChevronDown, IconCheck, IconX, IconAlertTriangle } from "@tabler/icons-react";
import type {
  CoverageQaDetectionReason,
  CoverageQaTaskReason,
  CoverageQaRejection,
} from "@/types/coverage";

interface CoverageQaActionBarProps {
  detectionConfirmed: boolean;
  hasChanges: boolean;
  hasProducts: boolean;
  isSubmitting: boolean;
  onDetectionOk: () => void;
  onSubmit: () => void;
  onReject: (rejection: CoverageQaRejection) => void;
}

const DETECTION_REASONS: { value: CoverageQaDetectionReason; label: string }[] = [
  { value: "wrong_class", label: "Wrong Class" },
  { value: "false_detection", label: "False Detection" },
];

const TASK_REASONS: { value: CoverageQaTaskReason; label: string }[] = [
  { value: "image_quality", label: "Image Quality" },
  { value: "obstructed_view", label: "Obstructed View" },
  { value: "bad_angle", label: "Bad Angle" },
  { value: "unsupported_planogram", label: "Unsupported Planogram" },
];

export const CoverageQaActionBar = ({
  detectionConfirmed,
  hasChanges,
  hasProducts,
  isSubmitting,
  onDetectionOk,
  onSubmit,
  onReject,
}: CoverageQaActionBarProps) => {
  return (
    <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-20 px-6 py-3">
      <div className="flex items-center justify-between max-w-screen-xl mx-auto">
        {/* Step 1: Detection review buttons */}
        <div className="flex items-center gap-2">
          {!detectionConfirmed ? (
            <>
              <Menu shadow="md" width={200} position="top-start">
                <Menu.Target>
                  <Button
                    variant="outline"
                    color="red"
                    rightSection={<IconChevronDown size={16} />}
                    disabled={isSubmitting}
                  >
                    Incorrect Detection
                  </Button>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Label>Select reason</Menu.Label>
                  {DETECTION_REASONS.map(({ value, label }) => (
                    <Menu.Item
                      key={value}
                      leftSection={<IconX size={14} />}
                      onClick={() => onReject({ response: "incorrect_detection", reason: value })}
                    >
                      {label}
                    </Menu.Item>
                  ))}
                </Menu.Dropdown>
              </Menu>

              <Menu shadow="md" width={220} position="top-start">
                <Menu.Target>
                  <Button
                    variant="outline"
                    color="orange"
                    rightSection={<IconChevronDown size={16} />}
                    disabled={isSubmitting}
                  >
                    Incorrect Task
                  </Button>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Label>Select reason</Menu.Label>
                  {TASK_REASONS.map(({ value, label }) => (
                    <Menu.Item
                      key={value}
                      leftSection={<IconAlertTriangle size={14} />}
                      onClick={() => onReject({ response: "incorrect_task", reason: value })}
                    >
                      {label}
                    </Menu.Item>
                  ))}
                </Menu.Dropdown>
              </Menu>

              <Button
                variant="light"
                color="green"
                leftSection={<IconCheck size={16} />}
                onClick={onDetectionOk}
                disabled={isSubmitting}
              >
                Detection OK
              </Button>
            </>
          ) : (
            <Button
              variant="subtle"
              color="green"
              leftSection={<IconCheck size={16} />}
              disabled
            >
              Detection Confirmed
            </Button>
          )}
        </div>

        {/* Step 2: Submit */}
        <Button
          size="md"
          leftSection={<IconCheck size={18} />}
          onClick={onSubmit}
          loading={isSubmitting}
          disabled={!detectionConfirmed || !hasProducts}
        >
          {hasChanges ? "Submit Correction" : "Submit"}
        </Button>
      </div>
    </div>
  );
};
